import { RouterProvider } from 'atomic-router-react';
import { useUnit } from 'effector-react';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import type { i18n } from 'i18next';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { inspectorToggled, sidebarToggled } from '../entities/preferences';
import { $sessionChecked, $user } from '../entities/session';
import { ClosePositionDialog } from '../features/close-position';
import { ConfirmDialog } from '../features/confirm';
import { ChartSymbolsDialog } from '../features/edit-chart-symbols';
import { EditTargetDialog } from '../features/edit-target';
import { AddInstrumentDialog } from '../features/manage-watchlist';
import { OpenPositionDialog, openPositionRequested } from '../features/open-position';
import { RecordFillDialog } from '../features/record-fill';
import { useHotkeys } from '../shared/lib/hotkeys';
import { router, routes } from '../shared/routing';
import { Button, EmptyState, ErrorBoundary, ToastProvider, TooltipProvider } from '../shared/ui';
import { HomePage } from '../pages/home';
import { CommandPalette, helpOpened, paletteOpened } from '../widgets/command-palette';
import { Toaster } from '../widgets/toaster';
import { usePreloadPages } from './preload';
import { RoutesView } from './routes-view';

export function App({ i18n }: { i18n: i18n }) {
  return (
    <I18nextProvider i18n={i18n}>
      <TooltipProvider>
        <ToastProvider swipeDirection="left">
          <RouterProvider router={router}>
            <ErrorBoundary fallback={() => <AppCrashed />}>
              <Gate />
            </ErrorBoundary>
          </RouterProvider>
          <Toaster />
        </ToastProvider>
      </TooltipProvider>
    </I18nextProvider>
  );
}

/**
 * Nothing renders until the session check answers, so private pages never flash. Signed out, every
 * URL shows the homepage; signing in then opens the URL that was asked for.
 */
function Gate() {
  const [checked, user] = useUnit([$sessionChecked, $user]);
  if (!checked) return null;
  if (!user) return <HomePage />;
  return (
    <>
      <RoutesView />
      <SignedInChrome />
    </>
  );
}

function SignedInChrome() {
  const [openPalette, openHelp, newPosition, toggleSidebar, toggleInspector] = useUnit([
    paletteOpened,
    helpOpened,
    openPositionRequested,
    sidebarToggled,
    inspectorToggled,
  ]);
  usePreloadPages();
  useHotkeys({
    'mod+k': () => {
      openPalette();
    },
    '?': () => {
      openHelp();
    },
    c: () => {
      newPosition({});
    },
    '[': () => {
      toggleSidebar();
    },
    ']': () => {
      toggleInspector();
    },
    'g p': () => void routes.positions.open(),
    'g a': () => void routes.alerts.open(),
    'g s': () => void routes.stats.open(),
    'g c': () => void routes.charts.open(),
    'g w': () => void routes.watchlist.open(),
    'g ,': () => void routes.settings.open(),
  });
  return (
    <>
      <CommandPalette />
      <OpenPositionDialog />
      <RecordFillDialog />
      <ClosePositionDialog />
      <EditTargetDialog />
      <AddInstrumentDialog />
      <ChartSymbolsDialog />
      <ConfirmDialog />
    </>
  );
}

function AppCrashed() {
  const { t } = useTranslation();
  return (
    <main className="flex h-full items-center justify-center bg-bg-0">
      <EmptyState
        icon={<TriangleAlert />}
        title={t('errors.appFailed')}
        body={t('errors.appFailedBody')}
        action={
          <Button
            variant="primary"
            leadingIcon={<RotateCcw />}
            onClick={() => {
              window.location.reload();
            }}
          >
            {t('errors.reload')}
          </Button>
        }
      />
    </main>
  );
}
